/**
 * InstallerReplaceModal — PROVISIONING-REPLACE-UX-2.
 *
 * Fluxo de substituição de Sonoff PARA O TÉCNICO no campo (InstallerPortal). Como
 * o técnico não tem uma tabela de bindings, ele informa OS DOIS QR físicos:
 * o do Sonoff atual/instalado e o do novo. Chama o mesmo endpoint do QR-6:
 * POST /api/admin/devices/bindings/{QR_atual}/replace { new_qr_serial, reason, confirm }.
 *
 * NÃO envia comando ao Sonoff. Reusa physicalQr (só dígitos), QrScannerModal e as
 * mensagens amigáveis de replaceBinding.
 */
import { useState } from "react";
import QrScannerModal from "./QrScannerModal.jsx";
import {
  isValidPhysicalQr,
  normalizePhysicalQr,
  PHYSICAL_QR_ERROR,
} from "../lib/physicalQr.js";
import { replaceErrorMessage } from "../lib/replaceBinding.js";
import { replaceDeviceBinding } from "../lib/api.js";

export default function InstallerReplaceModal({ onClose }) {
  const [currentQr, setCurrentQr] = useState("");
  const [newQr, setNewQr] = useState("");
  const [reason, setReason] = useState("");
  const [scanTarget, setScanTarget] = useState(null); // 'current' | 'new'
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [success, setSuccess] = useState(null);

  const nc = normalizePhysicalQr(currentQr);
  const nn = normalizePhysicalQr(newQr);
  const same = nc && nn && nc === nn;
  const canSubmit =
    !busy &&
    isValidPhysicalQr(currentQr) &&
    isValidPhysicalQr(newQr) &&
    reason.trim().length > 0 &&
    !same;

  async function submit() {
    if (!canSubmit) return;
    setBusy(true);
    setErr(null);
    try {
      await replaceDeviceBinding(nc, {
        new_qr_serial: nn,
        reason: reason.trim(),
        confirm: true,
      });
      setSuccess(
        "Substituição registrada. Ligue o novo Sonoff para concluir o vínculo automaticamente.",
      );
      setTimeout(onClose, 2200);
    } catch (e) {
      setErr(
        e?.status === 404
          ? "Vínculo não encontrado para o QR atual."
          : replaceErrorMessage(e),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-noc-bg border border-noc-border rounded-xl p-4 w-full max-w-sm space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-bold text-noc-text">Substituir equipamento</h3>
        <p className="text-[11px] text-noc-muted">
          Use esta opção quando estiver trocando um Sonoff já instalado por outro.
        </p>

        <div className="text-[11px] text-noc-accent bg-noc-accent/10 border border-noc-accent/30 rounded p-2 leading-relaxed">
          Esta ação <b>não envia comandos</b> ao equipamento antigo. O equipamento
          antigo será <b>arquivado</b> e o novo ficará <b>aguardando ligação/heartbeat</b>.
        </div>

        {success ? (
          <div className="text-[12px] text-noc-ok bg-noc-ok/10 border border-noc-ok/30 rounded p-2">
            {success}
          </div>
        ) : (
          <>
            <QrField
              label="QR físico do Sonoff atual"
              value={currentQr}
              onChange={setCurrentQr}
              onScan={() => setScanTarget("current")}
            />
            <QrField
              label="QR físico do novo Sonoff"
              value={newQr}
              onChange={setNewQr}
              onScan={() => setScanTarget("new")}
            />
            {same && (
              <p className="text-[10px] text-noc-warn">
                QR atual e QR novo não podem ser iguais.
              </p>
            )}

            <label className="block text-xs space-y-1">
              <span className="text-noc-muted">Motivo da troca</span>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ex.: defeito no relé / fonte / queima"
                className="w-full bg-noc-bg/60 border border-noc-border rounded px-2 py-1 text-noc-text"
              />
            </label>

            {err && (
              <div className="text-[11px] text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2">
                {err}
              </div>
            )}

            <div className="flex gap-2 justify-end pt-1">
              <button
                type="button"
                onClick={onClose}
                className="text-xs px-2.5 py-1.5 rounded border border-noc-border text-noc-muted"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={!canSubmit}
                className="text-xs px-3 py-1.5 rounded bg-noc-accent text-noc-bg font-semibold disabled:opacity-40"
              >
                {busy ? "Substituindo…" : "Confirmar substituição"}
              </button>
            </div>
          </>
        )}
      </div>

      {scanTarget && (
        <QrScannerModal
          onDetected={(v) => {
            const n = normalizePhysicalQr(v);
            if (scanTarget === "current") setCurrentQr(n);
            else setNewQr(n);
            setScanTarget(null);
          }}
          onManual={() => setScanTarget(null)}
          onClose={() => setScanTarget(null)}
        />
      )}
    </div>
  );
}

function QrField({ label, value, onChange, onScan }) {
  return (
    <label className="block text-xs space-y-1">
      <span className="text-noc-muted">{label}</span>
      <div className="flex gap-1">
        <input
          type="text"
          inputMode="numeric"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="25082200178100"
          className="flex-1 bg-noc-bg/60 border border-noc-border rounded px-2 py-1 font-mono text-noc-text"
        />
        <button
          type="button"
          onClick={onScan}
          className="text-xs px-2 py-1 rounded border border-noc-border text-noc-muted"
        >
          📷 Escanear
        </button>
      </div>
      {value && !isValidPhysicalQr(value) && (
        <p className="text-[10px] text-noc-warn">{PHYSICAL_QR_ERROR}</p>
      )}
    </label>
  );
}
